import { ref, watch } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { queryMockExcess } from '@/views/cromis/excess/excessMockData'

export default function useExcessList() {
    const toast = useToast()
    const records = ref(null)
    const usingMockData = ref(false)

    const columns = [
        { key: 'received_at', label: 'Date Received', sortable: true },
        { key: 'barcode', label: 'Barcode', sortable: false },
        { key: 'tfNumber', label: 'TF Received With', sortable: false },
        { key: 'purchased', label: 'Purch KG', sortable: false, thClass: 'text-right', tdClass: 'text-right' },
        { key: 'cromisWeight', label: 'Cromis KG', sortable: false, thClass: 'text-right', tdClass: 'text-right' },
        { key: 'receivedWeight', label: 'Recv KG', sortable: false, thClass: 'text-right', tdClass: 'text-right' },
        { key: 'variance', label: 'Variance', sortable: false, thClass: 'text-right', tdClass: 'text-right' },
        { key: 'scanner', label: 'Missed By', sortable: false },
        { key: 'confirmed', label: 'Status', sortable: false },
        { key: 'action', label: 'Action', sortable: false },
    ]

    const from = ref(0)
    const to = ref(0)
    const meta = ref({ from: 0, to: 0, total: 0 })
    const perPage = ref(10)
    const totalRecords = ref(0)
    const currentPage = ref(1)
    const perPageOptions = [5, 10, 25, 50, 100]
    const searchQuery = ref('')
    const sortBy = ref('received_at')
    const isSortDirDesc = ref(true)

    const refetch = () => {
        records.value.refresh()
    }

    watch([currentPage, perPage, searchQuery, sortBy, isSortDirDesc], () => {
        refetch()
    })

    const fetch = (ctx, callback) => {
        const params = {
            query: searchQuery.value,
            per_page: perPage.value,
            page: currentPage.value,
            sortBy: sortBy.value,
            sortDesc: isSortDirDesc.value,
        }

        store
            .dispatch('cromis-excess/list', params)
            .then(response => {
                const { excesses, meta: responseMeta } = response.data

                meta.value = responseMeta
                usingMockData.value = false

                callback(excesses)
                totalRecords.value = meta.value.total
                from.value = meta.value.from
                to.value = meta.value.to
            })
            .catch(() => {
                // Backend endpoint not reachable yet — fall back to demo data so the
                // screen remains usable for review/testing. Remove once the real
                // `excesses` API is live.
                const { excesses, meta: mockMeta } = queryMockExcess(params)

                meta.value = mockMeta
                if (!usingMockData.value) {
                    usingMockData.value = true
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: 'Showing demo data',
                            text: 'Excess bales API is not reachable — displaying sample data for preview.',
                            icon: 'InfoIcon',
                            variant: 'warning',
                        },
                    })
                }

                callback(excesses)
                totalRecords.value = meta.value.total
                from.value = meta.value.from
                to.value = meta.value.to
            })
    }

    const resolveStatusVariant = status => {
        if (`${status}` === '1') return 'success'
        if (`${status}` === '0') return 'secondary'
        if (`${status}` === '-1') return 'danger'
    }

    return {
        fetch,
        refetch,
        columns,
        perPage,
        currentPage,
        totalRecords,
        from,
        to,
        meta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        records,
        resolveStatusVariant,
        usingMockData,
    }
}
